import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Plugs,
  Faders,
  CaretLeft,
  ArrowsClockwise,
  SpinnerGap,
  XCircle,
  CaretDown,
  CaretRight,
  Cpu,
  CheckCircle,
  WarningCircle
} from '@phosphor-icons/react';
import OperationalStatusBadge from '@/components/OperationalStatusBadge';
import { OPERATIONAL_STATUS } from '@/constants/operationalTruth';
import apiClient from '@/api/client';

/**
 * Determine runtime connection status based strictly on the active session's connected AIs.
 * Never infer connection from catalog existence or operational status alone.
 */
const getRuntimeConnection = (connectorId, session) => {
  if (!session) return { isConnected: false, role: null };

  const normId = (connectorId || '').toLowerCase();
  const originProvider = (session.origin_ai?.provider || '').toLowerCase();
  const prodProvider = (session.production_ai?.provider || '').toLowerCase();

  const isOrigin = originProvider === normId || (normId === 'demo' && originProvider.startsWith('demo'));
  const isProd = prodProvider === normId || (normId === 'demo' && prodProvider.startsWith('demo'));

  if (isOrigin && isProd) {
    return { isConnected: true, role: 'Origin & Production' };
  }
  if (isOrigin) {
    return { isConnected: true, role: 'Origin AI' };
  }
  if (isProd) {
    return { isConnected: true, role: 'Production AI' };
  }
  return { isConnected: false, role: null };
};

const CollapsibleSidebar = ({
  isOpen,
  onToggle,
  activeFilter = 'all',
  onSelectFilter,
  session
}) => {
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [internalFilter, setInternalFilter] = useState(activeFilter);

  // Sync external activeFilter if changed
  useEffect(() => {
    if (activeFilter) {
      setInternalFilter(activeFilter);
    }
  }, [activeFilter]);

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/connectors/catalog');
      const data = response.data;
      if (Array.isArray(data)) {
        setCatalog(data);
      } else {
        setCatalog([]);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Error al consultar catálogo';
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
      setCatalog([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCatalog();
  }, [fetchCatalog]);

  const handleFilterClick = (filterId) => {
    setInternalFilter(filterId);
    if (typeof onSelectFilter === 'function') {
      onSelectFilter(filterId);
    }
  };

  const toggleDetails = (connectorId) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(connectorId)) {
        next.delete(connectorId);
      } else {
        next.add(connectorId);
      }
      return next;
    });
  };

  // Filter categories
  const filteredCatalog = useMemo(() => {
    if (internalFilter === 'all') return catalog;
    if (internalFilter === 'real') return catalog.filter((c) => c.operational_status === 'REAL');
    if (internalFilter === 'demo') return catalog.filter((c) => c.operational_status === 'DEMO');
    if (internalFilter === 'planned') return catalog.filter((c) => c.operational_status === 'PLANNED');
    if (internalFilter === 'executable') return catalog.filter((c) => c.can_execute === true);
    return catalog;
  }, [catalog, internalFilter]);

  const filterCounts = useMemo(() => {
    return {
      all: catalog.length,
      real: catalog.filter((c) => c.operational_status === 'REAL').length,
      demo: catalog.filter((c) => c.operational_status === 'DEMO').length,
      planned: catalog.filter((c) => c.operational_status === 'PLANNED').length,
      executable: catalog.filter((c) => c.can_execute === true).length
    };
  }, [catalog]);

  if (!isOpen) {
    return null;
  }

  return (
    <aside className="h-full w-80 shrink-0 border-r border-[#1F2937] bg-[#0A0C10] text-[#F3F4F6] p-3.5 flex flex-col gap-3 transition-all duration-300 min-h-0 overflow-hidden">
      {/* Header Bar */}
      <div className="flex items-center justify-between border-b border-[#1F2937] pb-2.5 shrink-0">
        <div className="flex items-center gap-2">
          <div className="p-1 bg-[#13131A] border border-[#1F2937] text-[#00E5FF] rounded">
            <Plugs className="w-4 h-4" />
          </div>
          <div>
            <h2 className="text-xs font-bold terminal-font text-[#F3F4F6] uppercase tracking-wider">
              Conectores
            </h2>
            <p className="text-[10px] font-mono text-[#9CA3AF]">
              Catálogo y Verdad Operacional
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={fetchCatalog}
            disabled={loading}
            className="p-1 hover:bg-[#13131A] text-[#9CA3AF] hover:text-[#00E5FF] rounded transition-colors"
            title="Recargar catálogo de conectores"
          >
            <ArrowsClockwise size={14} className={loading ? 'animate-spin text-[#00E5FF]' : ''} />
          </button>
          <button
            onClick={onToggle}
            className="p-1 hover:bg-[#13131A] text-[#9CA3AF] hover:text-[#00E5FF] rounded transition-colors"
            title="Colapsar panel"
          >
            <CaretLeft size={14} />
          </button>
        </div>
      </div>

      {/* Catalog Status Bar */}
      <div className="bg-[#13131A] border border-[#1F2937] px-3 py-2 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2 text-[11px] font-mono">
          <Cpu size={14} className="text-[#00E5FF]" />
          <span className="text-[#9CA3AF]">Catálogo:</span>
          <span className="text-[#F3F4F6] font-bold">
            {catalog.length > 0 ? `${catalog.length} registrados` : '0 registrados'}
          </span>
        </div>
        <OperationalStatusBadge status={OPERATIONAL_STATUS.REAL} size="sm" label="CATÁLOGO DINÁMICO" />
      </div>

      {/* Filter Mode Selector */}
      <div className="space-y-1.5 shrink-0">
        <div className="flex items-center justify-between text-[11px] font-mono text-[#9CA3AF] uppercase tracking-wider">
          <span>Filtrar por:</span>
          <span className="text-[10px] text-[#6B7280]">
            {filteredCatalog.length} de {catalog.length}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-1.5 text-xs">
          {[
            { id: 'all', label: 'Todos', count: filterCounts.all },
            { id: 'real', label: 'Real', count: filterCounts.real },
            { id: 'demo', label: 'Demo', count: filterCounts.demo },
            { id: 'planned', label: 'Planeados', count: filterCounts.planned },
            { id: 'executable', label: 'Ejecutables', count: filterCounts.executable }
          ].map((f) => (
            <button
              key={f.id}
              data-testid={`filter-btn-${f.id}`}
              onClick={() => handleFilterClick(f.id)}
              className={`px-2 py-1 font-mono text-center text-[10px] font-medium transition-all border rounded-xs flex items-center justify-center gap-1 ${
                internalFilter === f.id
                  ? 'bg-[#00E5FF]/15 text-[#00E5FF] border-[#00E5FF]/60 font-bold'
                  : 'bg-[#13131A] text-[#9CA3AF] border-[#1F2937] hover:bg-[#1C1C24] hover:text-[#F3F4F6]'
              }`}
            >
              <span>{f.label}</span>
              <span className={`text-[9px] px-1 rounded-full ${
                internalFilter === f.id ? 'bg-[#00E5FF]/30 text-[#00E5FF]' : 'bg-[#1C1C24] text-[#6B7280]'
              }`}>
                {f.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Scrollable Connectors List */}
      <div className="flex-1 overflow-y-auto space-y-2 pr-0.5 min-h-0" data-testid="connectors-catalog-list">
        {loading && catalog.length === 0 ? (
          <div className="py-12 flex flex-col items-center justify-center gap-2 text-[#9CA3AF]">
            <SpinnerGap size={20} className="animate-spin text-[#00E5FF]" />
            <span className="text-[11px] font-mono">Consultando catálogo de conectores...</span>
          </div>
        ) : error ? (
          <div className="p-3 bg-[#1C1014] border border-[#F85149]/30 rounded-xs text-xs font-mono space-y-2">
            <div className="flex items-center gap-1.5 font-bold text-[#F85149]">
              <XCircle size={15} />
              <span>Error de catálogo</span>
            </div>
            <p className="text-[11px] text-[#9CA3AF]">{error}</p>
            <button
              onClick={fetchCatalog}
              className="px-2 py-1 text-[10px] bg-[#1C1C24] hover:bg-[#252530] text-[#F3F4F6] border border-[#1F2937] rounded-xs"
            >
              Reintentar
            </button>
          </div>
        ) : filteredCatalog.length === 0 ? (
          <div className="py-10 text-center space-y-1.5 bg-[#13131A] border border-[#1F2937] p-4 rounded-xs">
            <Plugs size={24} className="mx-auto text-[#4B5563]" />
            <p className="text-xs font-mono text-[#F3F4F6] font-medium">
              No hay conectores para el filtro seleccionado.
            </p>
            <p className="text-[10px] text-[#9CA3AF]">
              Selecciona otro filtro para ver los conectores registrados.
            </p>
          </div>
        ) : (
          filteredCatalog.map((item) => {
            const { isConnected, role } = getRuntimeConnection(item.id, session);
            const isExpanded = expandedIds.has(item.id);

            return (
              <div
                key={item.id}
                data-testid={`connector-card-${item.id}`}
                className="bg-[#13131A] hover:bg-[#181822] border border-[#1F2937] hover:border-[#00E5FF]/30 rounded-xs p-2.5 transition-all text-xs space-y-2"
              >
                {/* Connector Top Info */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-mono font-bold text-[#F3F4F6] text-xs truncate">
                        {item.display_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-[#9CA3AF] font-mono mt-0.5 flex-wrap">
                      <span className="text-[#6B7280]">id: {item.id}</span>
                      <span>•</span>
                      <span>Modos: {item.supported_modes?.join(', ') || '-'}</span>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div data-testid={`connector-status-${item.id}`} className="shrink-0">
                    <OperationalStatusBadge
                      status={
                        item.operational_status === 'REAL'
                          ? OPERATIONAL_STATUS.REAL
                          : item.operational_status === 'DEMO'
                          ? OPERATIONAL_STATUS.DEMO
                          : OPERATIONAL_STATUS.PLANNED
                      }
                      size="sm"
                    />
                  </div>
                </div>

                {/* Capabilities & Connection State */}
                <div className="flex items-center justify-between gap-2 pt-1 border-t border-[#1F2937]/50 text-[10px] font-mono">
                  {/* Execution capability badge */}
                  <div data-testid={`connector-exec-${item.id}`}>
                    {item.can_execute ? (
                      <span className="inline-flex items-center gap-1 text-[#10B981] bg-[#10B981]/10 px-1.5 py-0.5 rounded-xs border border-[#10B981]/30">
                        <CheckCircle size={11} weight="bold" /> EJECUTABLE
                      </span>
                    ) : item.can_verify ? (
                      <span className="inline-flex items-center gap-1 text-[#00E5FF] bg-[#00E5FF]/10 px-1.5 py-0.5 rounded-xs border border-[#00E5FF]/30">
                        <WarningCircle size={11} /> VERIFICABLE
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[#6B7280] bg-[#1C1C24] px-1.5 py-0.5 rounded-xs border border-[#1F2937]">
                        NO EJECUTABLE
                      </span>
                    )}
                  </div>

                  {/* Runtime connection badge */}
                  <div data-testid={`connector-conn-${item.id}`}>
                    {isConnected ? (
                      <span className="inline-flex items-center gap-1 text-[#00E5FF] bg-[#00E5FF]/15 px-1.5 py-0.5 rounded-xs border border-[#00E5FF]/40 font-bold">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] animate-pulse" />
                        {role}
                      </span>
                    ) : (
                      <span className="text-[#6B7280]">Desconectado</span>
                    )}
                  </div>
                </div>

                {/* Progressive disclosure details */}
                <button
                  type="button"
                  onClick={() => toggleDetails(item.id)}
                  className="text-[10px] font-mono text-[#00E5FF] hover:text-[#00E5FF]/80 flex items-center gap-1 pt-0.5 transition-colors"
                >
                  {isExpanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
                  {isExpanded ? 'Ocultar detalles técnicos' : 'Ver detalles técnicos'}
                </button>

                {isExpanded && (
                  <div className="bg-[#0A0C10] border border-[#1F2937] p-2 space-y-1 text-[10px] font-mono text-[#9CA3AF] rounded-xs mt-1">
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Requiere API Key:</span>
                      <span className="text-[#F3F4F6]">{item.requires_api_key ? 'Sí' : 'No'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Verificación:</span>
                      <span className="text-[#F3F4F6]">
                        {item.can_verify ? 'Disponible' : 'No disponible'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Ejecución:</span>
                      <span className="text-[#F3F4F6]">
                        {item.can_execute ? 'Disponible' : 'No disponible'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#6B7280]">Adapter Disponibilidad:</span>
                      <span className="text-[#F3F4F6]">
                        {item.adapter_available ? 'Disponible' : 'Pendiente'}
                      </span>
                    </div>
                    {item.reason && (
                      <div className="pt-1 border-t border-[#1F2937] text-[9px] text-[#D97706]">
                        Nota: {item.reason}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </aside>
  );
};

export default CollapsibleSidebar;
